import type { DispatchMetric } from '../../api/types';
import { PRESET_PACKS, isPresent, packStatus } from './presets';

/**
 * The ready-made half of the new-metric dialog: packs of curated metrics with
 * a checkbox each.
 *
 * Controlled, because the dialog's footer is what acts on the selection and a
 * button that cannot see what is ticked cannot say how many it is about to
 * add. So this draws and reports; `MetricDialog` owns the picks.
 *
 * Two things here are deliberate. A preset the dashboard ALREADY carries is
 * shown ticked and disabled rather than hidden: a pack that silently shrinks
 * reads as a different pack every time you open it, and the point of the list
 * is to say what a complete set looks like. And the essentials arrive ticked
 * only on a dashboard with nothing on it — where somebody has already built
 * one, a tab that pre-selects six cards is one stray click from six to delete.
 */
export function PresetPicker({
  metrics,
  picked,
  busy,
  onPick,
}: {
  metrics: DispatchMetric[];
  picked: Set<string>;
  busy: boolean;
  onPick: (next: Set<string>) => void;
}) {
  const toggle = (id: string, on: boolean) => {
    const next = new Set(picked);
    if (on) next.add(id);
    else next.delete(id);
    onPick(next);
  };

  return (
    <>
      <p className="db-preset-lede">
        Sets most operators start from. Ticking one saves it to dispatch like any other metric, so it
        appears on the dispatch dashboard too — it is one list.
      </p>

      {PRESET_PACKS.map((pack) => {
        const status = packStatus(pack, metrics);
        const missingIds = status.missing.map((preset) => preset.id);
        const allPicked = missingIds.length > 0 && missingIds.every((id) => picked.has(id));

        return (
          <section key={pack.id} className="db-preset-pack">
            <header className="db-preset-head">
              <div>
                <h3>{pack.label}</h3>
                <p>{pack.blurb}</p>
              </div>
              {missingIds.length ? (
                <button
                  type="button"
                  className="btn btn-secondary db-preset-all"
                  disabled={busy}
                  onClick={() => {
                    const next = new Set(picked);
                    for (const id of missingIds) {
                      if (allPicked) next.delete(id);
                      else next.add(id);
                    }
                    onPick(next);
                  }}
                >
                  {allPicked ? 'None' : `All ${missingIds.length}`}
                </button>
              ) : (
                <span className="db-preset-done">Added</span>
              )}
            </header>

            <div className="mselect">
              {pack.presets.map((preset) => {
                const already = isPresent(preset, metrics);
                return (
                  <label
                    key={preset.id}
                    className={already ? 'mselect-row is-off' : 'mselect-row'}
                    title={already ? 'This dashboard already has it' : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={already || picked.has(preset.id)}
                      disabled={busy || already}
                      onChange={(event) => toggle(preset.id, event.target.checked)}
                    />
                    <span>
                      <span className="mselect-name">{preset.write.metricsName}</span>
                      <span className="mselect-sub">
                        {' · '}
                        {already ? 'already added' : preset.blurb}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}
    </>
  );
}
